<template>
  <master-layout no-bottom-pad>
    <div class="relative w-screen h-screen flex justify-center bg-grey-10">
      <PhotosModal :show="showModal" @close-modal="closeModal"></PhotosModal>
      <PastGicsModal :show="showPastGicsModal" @close-modal="closePastGicsModal"></PastGicsModal>
      <!-- Sticky header — always visible -->
      <div
        ref="stickyHeader"
        class="photodrop-sticky-header fixed w-full lg:w-[calc(100vw-292px)] lg:right-0 px-6 pt-4 bg-grey-10 z-[4]"
        :class="{ '!pt-[60px]': isIos, '!pt-[max(16px,env(safe-area-inset-top))]': isAndroid }"
      >
        <div
          class="header-widgets flex flex-row mx-auto justify-between items-center pb-5 lg:max-w-[964px]"
        >
          <BackButton
            variant="bare"
            :is-gic="true"
            data-test-id="gensapp-photodrop-back"
            @click="back"
          />

          <div class="flex items-center gap-2">
            <!-- Download All button (hidden when selecting) -->
            <BaseButton
              v-if="!isSelectingPhotos"
              :solid-classes="`!rounded-[100px] items-center h-9 !bg-transparent !border-2 !border-solid !border-orange text-orange font-aktiv-grotesk-medium !text-[14px] !leading-[24px] text-center`"
              :disabled="isDownloading"
              @click="handleDownloadAll"
            >
              {{ isDownloading ? downloadProgressText : 'Download All' }}
            </BaseButton>

            <!-- Download selected button (shown when selecting) -->
            <BaseButton
              v-if="isSelectingPhotos"
              :solid-classes="`!rounded-[100px] items-center h-9 bg-orange text-white !text-[14px] !leading-[20px]`"
              :disabled="selectedPhotos.length === 0 || isDownloading"
              :data-test-id="'gensapp-photodrop-download'"
              @click="handleDownload"
            >
              {{ isDownloading ? downloadProgressText : `Download (${selectedPhotos.length})` }}
            </BaseButton>

            <!-- My QR, Select buttons -->
            <PhotosHeader :is-my-photos-tab="false" @open-modal="openModal" />
          </div>
        </div>

        <div
          class="w-full lg:max-w-[964px] mx-auto text-left font-aktiv-grotesk-medium text-[24px] leading-[32px] text-[#21252C] pb-5"
        >
          GIC 4
        </div>
      </div>

      <!-- Gray loading (initial fetch or day/tab refetch) — measured top aligns with header on Android -->
      <div
        v-show="isInitialLoading || contentLoading"
        class="absolute inset-x-0 bottom-0 bg-grey-10 z-[3]"
        :style="{ top: stickyHeaderHeight + 'px' }"
      ></div>

      <!-- Content area — visible once initial load is done -->
      <div v-show="!isInitialLoading" class="w-full">
        <div class="flex flex-col w-full" :style="{ paddingTop: stickyHeaderHeight + 'px' }">
          <PhotoEveryoneDayTab
            class="fixed z-[4] left-0 lg:left-[292px]"
            :style="{ top: stickyHeaderBottom + 'px' }"
            ref="photoEveryOneDayTab"
            :show-past-gics="true"
            :past-gics-active="showPastGicsModal"
            :schedule-dates-override="GIC4ScheduleDates"
            @open-past-gics-modal="openPastGicsModal"
          />

          <!-- Photo grid — hidden while content loading (gray panel above handles loading UI) -->
          <div
            v-show="!contentLoading"
            class="flex flex-col bg-white pb-12 md:pb-[32px] lg:w-[calc(100vw-292px)]"
            :style="{ marginTop: subTabHeight + 'px' }"
          >
            <div class="flex flex-col mx-2 pb-24 lg:p-0 md:mb-8">
              <div
                class="shadow-ambient flex flex-col md:flex-row w-full md:max-w-[478px] lg:max-w-[964px] mx-auto md:justify-center"
              >
                <PhotosEveryone />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import platformMixin from '@/mixins/platformMixin';
import photoDownloadMixin from '@/mixins/photoDownloadMixin';
import { forEveryoneDaySlugs } from '@/utils/photodropPageSlugs';
import BaseButton from '../../components/common/BaseButton.vue';
import BackButton from '@/components/common/BackButton.vue';
import PhotosHeader from '@/components/Photo/PhotosHeader';
import PhotosEveryone from '@/components/Photo/PhotosEveryone';
import PhotosModal from '@/components/Photo/PhotosModal';
import PastGicsModal from '@/components/Photo/PastGicsModal';
import PhotoEveryoneDayTab from '@/components/Photo/PhotoEveryoneDayTab';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { trackPhotodropDownloadAll, trackPhotodropBulkDownload } from '@/utils/photodrop-analytics';

const TAB = {
  EVERYONE: {
    displayText: 'For everyone',
  },
};

const TABS = Object.values(TAB);

export default {
  mixins: [platformMixin, photoDownloadMixin],
  components: {
    BaseButton,
    BackButton,
    PhotosModal,
    PastGicsModal,
    PhotosEveryone,
    PhotosHeader,
    PhotoEveryoneDayTab,
  },
  layout: 'photos',
  data() {
    return {
      stickyHeaderHeight: 76,
      stickyHeaderBottom: 76,
      subTabHeight: 92,
      viewportWidth: null,
      selectedTab: TAB.EVERYONE,
      tab: TAB,
      showModal: false,
      showPastGicsModal: false,
      forEveryoneDaySlugs: forEveryoneDaySlugs,
      forEveryoneDaySlugMappings: {},
    };
  },
  computed: {
    ...mapState('photos', [
      'photos',
      'everyonePhotos',
      'isSelectingPhotos',
      'selectedPhotos',
      'loading',
      'contentLoading',
      'selectedDayTabGIC',
    ]),
    ...mapGetters('schedule', ['GIC4ScheduleDates']),
    ...mapGetters('profile', ['isAbsentAlumni']),
    ...mapState('user', ['token']),
    isInitialLoading() {
      return this.loading && this.photos.length === 0 && this.everyonePhotos.length === 0;
    },
    scheduleDatesComputed() {
      return this.GIC4ScheduleDates;
    },
    tabs() {
      return TABS.map(tab => {
        return {
          displayText: tab.displayText,
          translatedText: (tab.displayText = this.$t('copy.photoDropPage.forEveryoneTab.tabName')),
        };
      });
    },
    isWhichDayAnchorTagPresentInTheUrl() {
      if (this.$route.hash) {
        return this.forEveryoneDaySlugs.includes(this.$route.hash.slice(1));
      }
      return false;
    },
  },
  watch: {
    isInitialLoading(newVal) {
      if (!newVal) {
        this.$nextTick(() => this.measureLayout());
      }
    },
    '$store.state.photos.retryTrigger'(newVal, oldVal) {
      if (newVal > oldVal && this.$store.state.photos.failedUrls.length) {
        this.downloadPhotos([...this.$store.state.photos.failedUrls], true);
      }
    },
    '$route.hash': {
      handler(newHash, oldHash) {
        console.log('Hash changed from', oldHash, 'to', newHash);

        if (newHash && newHash !== oldHash) {
          this.$nextTick(() => {
            this.handleAnchorTagNavigation();
          });
        }
      },
      immediate: false,
    },
  },
  async ionViewWillEnter() {
    this.$store.dispatch('cms/fetchGicPhotodropForEveryone');
    this.$store.commit('photos/setEveryonePhotos', []);
    await this.populateForEveryoneDaySlugMappings();
    // default to first day if user has not selected any day tabs before
    if (!this.scheduleDatesComputed.includes(this.selectedDayTabGIC)) {
      const dayOneSlug = this.forEveryoneDaySlugMappings[this.forEveryoneDaySlugs[0]];
      await this.setSelectedDayTabGIC(dayOneSlug);
    } else {
      await this.setSelectedDayTabGIC(this.selectedDayTabGIC);
    }
    await this.$store.dispatch('photos/fetchPhotosEveryone', {
      tabName: this.selectedDayTabGIC,
      conferenceType: 'GIC',
    });
  },
  async ionViewDidEnter() {
    this.measureLayout();
    await this.handleAnchorTagNavigation();

    FirebaseAnalytics.setScreenName({
      screenName: 'photodrop - for everyone',
      nameOverride: 'PhotoDropPage - For Everyone',
    });
  },
  async ionViewDidLeave() {
    this.onSelectTab(this.tabs[0]);
  },
  mounted() {
    this.viewportWidth = window.innerWidth;
    window.addEventListener('resize', this.checkViewportWidth);
    this.measureLayout();
    this.$nextTick(() => {
      if (this.$refs.stickyHeader && window.ResizeObserver) {
        this._headerObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this._headerResizeFrame);
          this._headerResizeFrame = requestAnimationFrame(() => {
            this._headerResizeFrame = null;
            this.measureLayout();
          });
        });
        this._headerObserver.observe(this.$refs.stickyHeader);
      }
    });
  },
  unmounted() {
    window.removeEventListener('resize', this.checkViewportWidth);
    if (this._headerObserver) this._headerObserver.disconnect();
    cancelAnimationFrame(this._headerResizeFrame);
  },
  methods: {
    ...mapActions('photos', ['setSelectedDayTabGIC']),
    checkViewportWidth() {
      this.viewportWidth = window.innerWidth;
      this.measureLayout();
    },
    measureLayout() {
      this.$nextTick(() => {
        if (this.$refs.stickyHeader) {
          this.stickyHeaderHeight = this.$refs.stickyHeader.offsetHeight;
          this.stickyHeaderBottom = this.$refs.stickyHeader.getBoundingClientRect().bottom;
        }
        const subTab = this.$refs.photoEveryOneDayTab?.$el;
        if (subTab) this.subTabHeight = subTab.offsetHeight;
      });
    },
    back() {
      this.$router.push('/gic/home');
    },
    resetSelectPhotos() {
      this.$store.dispatch('photos/updateIsSelectingPhotos', false);
      this.$store.dispatch('photos/resetSelectedPhotos');
    },
    async onSelectTab(tab) {
      this.selectedTab = tab;
      this.resetSelectPhotos();
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'photodrop-for-everyone',
          page: 'photodrop',
        },
      });
      await this.$store.dispatch('photos/fetchPhotosEveryone', {
        tabName: this.selectedDayTabGIC,
        conferenceType: 'GIC',
      });
      FirebaseAnalytics.setScreenName({
        screenName: 'photodrop - for everyone',
        nameOverride: 'PhotoDropPage - For Everyone',
      });
    },
    closeModal() {
      this.showModal = false;
    },
    openModal() {
      this.showModal = true;
    },
    closePastGicsModal() {
      this.showPastGicsModal = false;
    },
    openPastGicsModal() {
      this.showPastGicsModal = true;
    },
    handleDownload() {
      if (!this.selectedPhotos.length) return;
      this._trackDownload(this.selectedPhotos);
      this.downloadPhotos(this._resolveSelectedForDownload(this.selectedPhotos));
    },
    handleDownloadAll() {
      const photos = this.$store.state.photos.everyonePhotos;
      trackPhotodropDownloadAll(this.$route.path, 'for_everyone', photos.length);
      this.downloadAllPhotos(this.selectedDayTabGIC, 'GIC');
    },
    _trackDownload(urls) {
      urls.forEach(url => {
        try {
          const result = new URL(url).pathname;
          FirebaseAnalytics.logEvent({
            name: 'file_downloaded_for_everyone',
            params: { file_name: result },
          });
        } catch (e) {
          /* noop */
        }
        try {
          window.dataLayer.push({
            event: 'file-downloaded-web-for-everyone',
            file_name: new URL(url).pathname,
          });
        } catch (e) {
          /* noop */
        }
      });
      try {
        FirebaseAnalytics.logEvent({
          name: 'click_button',
          params: {
            button_name: 'photodrop-download-button-for-everyone',
            page: 'photodrop',
            file_quantity: urls.length,
          },
        });
      } catch (e) {
        /* noop */
      }
      try {
        window.dataLayer.push({
          event: 'photodrop-download-web-for-everyone',
          file_quantity: urls.length,
          page_section: 'for-everyone',
        });
      } catch (e) {
        /* noop */
      }
      trackPhotodropBulkDownload(this.$route.path, 'direct', urls.length, 'for_everyone');
    },
    async populateForEveryoneDaySlugMappings() {
      this.forEveryoneDaySlugs.forEach((slug, index) => {
        if (this.scheduleDatesComputed[index]) {
          this.forEveryoneDaySlugMappings[slug] = this.scheduleDatesComputed[index];
        } else {
          this.forEveryoneDaySlugMappings[slug] = 'Featured';
        }
      });
    },
    getDateFromAnchorTag() {
      const hash = this.$route.hash.slice(1);
      return this.forEveryoneDaySlugMappings[hash];
    },
    async handleAnchorTagNavigation() {
      if (this.isWhichDayAnchorTagPresentInTheUrl) {
        await this.$refs.photoEveryOneDayTab.changeDayTab(this.getDateFromAnchorTag());
      }
    },
  },
};
</script>

<style>
.qr-container {
  /* background-image: linear-gradient(to bottom right, #FFD900 12%,#FFD900, #FFD900, #FFD900); */
  background-color: #ffd900;
}

.tab-card {
  box-shadow: 0px -3px 20px rgba(0, 0, 0, 0.08);
}

ion-refresher.refresher-native {
  display: block;
  z-index: 100;
}
</style>
